import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input } from '@nextui-org/react';

const DELETE_CONFIRM_WORD = 'delete';
export interface SimpleDeleteConfirmationModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  entityName?: string;
  body?: ReactNode;
  secondaryMessage?: ReactNode;
  modalHeaderClassName?: string;
  modalBodyClassName?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  onConfirm: () => void | Promise<void>;
  isConfirmLoading?: boolean;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  modalTestId?: string;
  cancelButtonTestId?: string;
  confirmButtonTestId?: string;
  isDismissable?: boolean;
  /** Word the user must type to enable the confirm button. Defaults to "delete". */
  confirmWord?: string;
  /** Modal header/title. Defaults to "Confirm deletion". */
  title?: ReactNode;
  /** Color of the confirm button. Defaults to "danger". */
  confirmColor?: 'danger' | 'warning' | 'primary' | 'success' | 'default';
  /** When false, the "type to confirm" input is hidden and the confirm button is always enabled. Defaults to true. */
  requireTypedConfirmation?: boolean;
}

export function SimpleDeleteConfirmationModal({
  isOpen,
  onOpenChange,
  entityName,
  body,
  secondaryMessage,
  modalHeaderClassName = 'flex flex-col gap-1',
  modalBodyClassName,
  size,
  onConfirm,
  isConfirmLoading = false,
  confirmLabel,
  cancelLabel,
  modalTestId,
  cancelButtonTestId,
  confirmButtonTestId,
  isDismissable = true,
  confirmWord = DELETE_CONFIRM_WORD,
  title,
  confirmColor = 'danger',
  requireTypedConfirmation = true,
}: SimpleDeleteConfirmationModalProps) {
  const { t } = useTranslation();
  const [confirmInput, setConfirmInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      setConfirmInput('');
    }
  }, [isOpen]);

  const normalizedConfirmWord = confirmWord.trim().toLowerCase();
  const canConfirm = !requireTypedConfirmation || confirmInput.trim().toLowerCase() === normalizedConfirmWord;

  const handleConfirmPress = useCallback(() => {
    if (!canConfirm) return;
    void onConfirm();
  }, [onConfirm, canConfirm]);

  const resolvedCancelLabel = cancelLabel ?? t('Close');
  const resolvedConfirmLabel = confirmLabel ?? t('Delete');

  const trimmedEntityName = entityName?.trim() ?? '';
  const defaultBody =
    trimmedEntityName.length > 0 ? (
      <p className="text-sm">
        {t('Do you want to delete')}{' '}
        <span className="font-semibold">{trimmedEntityName}</span>
        {'? '}
        {t('This will be permanently removed from your workspace.')}
      </p>
    ) : null;

  const modalBodyContent =
    body ??
    (defaultBody ? (
      <>
        {defaultBody}
        {secondaryMessage ? (
          <p className="text-sm text-default-500 mt-2">{secondaryMessage}</p>
        ) : null}
      </>
    ) : null);

  return (
    <Modal
      data-testid={modalTestId}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      size={size}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className={modalHeaderClassName}>{title ?? t('Confirm deletion')}</ModalHeader>
            <ModalBody className={modalBodyClassName}>
              {modalBodyContent}
              {requireTypedConfirmation && (
                <div className="flex flex-col gap-2 mt-4">
                  <p className="text-sm text-default-600">{t('Type "{{word}}" to confirm', { word: confirmWord })}</p>
                  <Input
                    data-testid="delete-confirmation-input"
                    aria-label={t('Type "{{word}}" to confirm', { word: confirmWord })}
                    placeholder={confirmWord}
                    value={confirmInput}
                    onValueChange={setConfirmInput}
                    isDisabled={isConfirmLoading}
                    size="sm"
                    variant="bordered"
                  />
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button data-testid={cancelButtonTestId} variant="light" onPress={onClose}>
                {resolvedCancelLabel}
              </Button>
              <Button
                data-testid={confirmButtonTestId}
                color={confirmColor}
                onPress={handleConfirmPress}
                isLoading={isConfirmLoading}
                isDisabled={!canConfirm}
              >
                {resolvedConfirmLabel}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}